/*
 * Sudoku engine, part 3 of 3 — making puzzles and judging foreign ones.
 *
 *   SudokuEngine.LEVELS                     what each level of each board size demands
 *   SudokuEngine.generate({size, level, seed})   one puzzle; the same seed → the same puzzle
 *   SudokuEngine.dailySeed(size, level, day)     the seed of "the puzzle of the day"
 *   SudokuEngine.analyze(values)            a pasted puzzle: no solution / several / one + its grade
 *   SudokuEngine.levelOfTier(tier)          1…5 → easy…master, above → extreme
 *
 * How a puzzle is made. A full grid is dealt from the seed, then clues are
 * taken away in symmetric groups — and a group stays removed only while the
 * LOGIC solver, limited to the level's techniques, still finishes the board.
 * So the puzzle is solvable without guessing by construction, and its single
 * solution follows (every technique is a proof); the exhaustive solver
 * confirms it once more before the puzzle is handed out. A puzzle is accepted
 * when the hardest technique it needs is the level's own — a "hard" that a
 * person solves with singles alone is a mislabelled "medium".
 *
 * Only integers and GameKit.rng, never the browser's own random source: a shared link and a daily
 * puzzle must rebuild the very same board on another device.
 */
(function (root) {
    'use strict';

    var S = root.SudokuEngine;

    // tier  — the hardest techniques allowed (see TECHNIQUES in logic.js)
    // need  — the tier the puzzle must actually require
    // floor — never fewer clues than this
    // pick  — small boards only: take the most demanding of this many puzzles.
    //         A 6×6 that truly needs a pair is one deal in hundreds (measured:
    //         13 of 30 seeds found none in 400 deals), so the small boards are
    //         levelled by how few clues are left and which techniques are
    //         allowed, and `pick` leans each level towards its harder deals.
    var LEVELS = {
        9: {
            easy:   { tier: 1, need: 1, floor: 36 },
            medium: { tier: 2, need: 2, floor: 30 },
            hard:   { tier: 3, need: 3, floor: 26 },
            expert: { tier: 4, need: 4, floor: 22 },
            master: { tier: 5, need: 5, floor: 22 }
        },
        6: {
            easy:   { tier: 1, need: 0, floor: 18, pick: 1 },
            medium: { tier: 2, need: 0, floor: 13, pick: 6 },
            hard:   { tier: 3, need: 0, floor: 8, pick: 16 }
        },
        4: {
            easy:   { tier: 1, need: 0, floor: 8, pick: 1 },
            medium: { tier: 2, need: 0, floor: 4, pick: 4 }
        }
    };
    var LEVEL_ORDER = ['easy', 'medium', 'hard', 'expert', 'master'];

    function levelOfTier(tier) {
        return tier >= 1 && tier <= 5 ? LEVEL_ORDER[tier - 1] : 'extreme';
    }

    // ── Clue layouts ─────────────────────────────────────────────────────────
    // Clues leave in groups that map onto each other, so the printed pattern
    // is symmetric — the look of a puzzle set by hand.
    var SYMMETRY = ['rot180', 'diag', 'anti', 'mirror_h', 'mirror_v', 'rot90'];

    function images(n, sym, r, c) {
        var m = n - 1;
        switch (sym) {
            case 'rot180': return [[r, c], [m - r, m - c]];
            case 'diag': return [[r, c], [c, r]];
            case 'anti': return [[r, c], [m - c, m - r]];
            case 'mirror_h': return [[r, c], [r, m - c]];
            case 'mirror_v': return [[r, c], [m - r, c]];
            case 'rot90': return [[r, c], [c, m - r], [m - r, m - c], [m - c, r]];
        }
        return [[r, c]];
    }

    function orbits(geo, sym) {
        var n = geo.n, seen = new Uint8Array(geo.cells), out = [];
        for (var i = 0; i < geo.cells; i++) {
            if (seen[i]) continue;
            var img = images(n, sym, (i / n) | 0, i % n), group = [];
            for (var k = 0; k < img.length; k++) {
                var cell = img[k][0] * n + img[k][1];
                if (!seen[cell]) { seen[cell] = 1; group.push(cell); }
            }
            out.push(group);
        }
        return out;
    }

    // Half-turn symmetry is the newspaper classic and gets most of the draws;
    // the quarter-turn one needs many clues, so only the generous levels use it.
    function pickSymmetry(rnd, n, spec) {
        var roll = rnd.int(100);
        if (roll < 56) return 'rot180';
        if (roll < 68) return 'diag';
        if (roll < 78) return 'anti';
        if (roll < 86) return 'mirror_h';
        if (roll < 94) return 'mirror_v';
        return (spec.tier <= 2 && n === 9) ? 'rot90' : 'rot180';
    }

    function seedOf(seed) {
        return (typeof seed === 'string') ? root.GameKit.hash(seed) : (seed >>> 0);
    }

    function dailySeed(size, level, day) {
        return root.GameKit.dailySeed(day, 'sudoku:' + size + ':' + level);
    }

    // One attempt: deal a grid, dig it, grade what is left.
    function attempt(geo, spec, rnd) {
        var cells = geo.cells, blank = [];
        for (var i = 0; i < cells; i++) blank.push(0);
        var full = S.solve(geo, blank, { limit: 1, rng: rnd.fork('grid') }).solution;
        var layout = rnd.fork('layout');
        var sym = pickSymmetry(layout, geo.n, spec);
        var groups = layout.shuffle(orbits(geo, sym));
        var puzzle = full.slice(), clues = cells, g, k;
        for (g = 0; g < groups.length; g++) {
            var group = groups[g];
            if (clues - group.length < spec.floor) continue;
            for (k = 0; k < group.length; k++) puzzle[group[k]] = 0;
            if (S.grade(geo, puzzle, { maxTier: spec.tier }).solved) {
                clues -= group.length;
            } else {
                for (k = 0; k < group.length; k++) puzzle[group[k]] = full[group[k]];
            }
        }
        var graded = S.grade(geo, puzzle, { maxTier: spec.tier });
        return { puzzle: puzzle, solution: full, clues: clues, symmetry: sym, grade: graded };
    }

    function generate(opts) {
        opts = opts || {};
        var size = opts.size || 9, level = opts.level || 'easy';
        var table = LEVELS[size];
        if (!table || !table[level]) throw new Error('SudokuEngine: no level ' + level + ' for size ' + size);
        var spec = table[level], geo = S.geometry(size);
        var seed = seedOf(opts.seed == null ? 1 : opts.seed);
        var base = root.GameKit.rng(seed);
        var maxAttempts = opts.maxAttempts || 400, fallback = null, tries = 0, a, res;
        if (spec.pick) {
            var chosen = null;
            for (a = 0; a < spec.pick; a++) {
                tries++;
                res = attempt(geo, spec, base.fork('try' + a));
                if (!res.grade.solved || S.solve(geo, res.puzzle, { limit: 2 }).count !== 1) continue;
                var better = !chosen || res.grade.tier > chosen.grade.tier ||
                    (res.grade.tier === chosen.grade.tier && res.grade.score > chosen.grade.score);
                if (better) chosen = res;
            }
            return chosen ? pack(size, level, seed, chosen, tries, true) : null;
        }
        for (a = 0; a < maxAttempts; a++) {
            tries++;
            res = attempt(geo, spec, base.fork('try' + a));
            if (!res.grade.solved) continue;
            var exact = !spec.need || res.grade.tier === spec.need;
            if (!exact && (!fallback || res.grade.tier > fallback.grade.tier)) fallback = res;
            if (!exact) continue;
            if (S.solve(geo, res.puzzle, { limit: 2 }).count !== 1) continue;
            return pack(size, level, seed, res, tries, true);
        }
        // Not reached in practice (the tests count on it), but a caller must
        // always get a playable board: the closest lower grade, labelled as such.
        return fallback ? pack(size, levelOfTier(fallback.grade.tier), seed, fallback, tries, false) : null;
    }

    function pack(size, level, seed, res, tries, exact) {
        return {
            size: size, level: level, seed: seed, exact: exact,
            givens: S.format(res.puzzle, '0'), solution: S.format(res.solution, '0'),
            clues: res.clues, symmetry: res.symmetry, attempts: tries,
            tier: res.grade.tier, techs: res.grade.techs, score: res.grade.score
        };
    }

    // ── A puzzle from a newspaper ────────────────────────────────────────────
    // status: 'conflict' (a digit repeats) · 'none' (no solution) · 'many'
    // (several; `differ` lists the cells where two of them part ways — the
    // place to look for a mistyped or missing clue) · 'unknown' (the search
    // was cut off) · 'ok' (one solution, graded).
    function analyze(values, size) {
        var geo = S.geometry(size || Math.round(Math.sqrt(values.length)));
        var out = { status: 'ok', size: geo.n, clues: 0, conflicts: [], differ: [], solution: null, grade: null, level: null };
        for (var i = 0; i < geo.cells; i++) if (values[i]) out.clues++;
        out.conflicts = S.conflicts(geo, values);
        if (out.conflicts.length) { out.status = 'conflict'; return out; }
        var found = S.solve(geo, values, { limit: 2 });
        if (found.capped && found.count < 2) { out.status = 'unknown'; return out; }
        if (found.count === 0) { out.status = 'none'; return out; }
        if (found.count > 1) {
            out.status = 'many';
            for (i = 0; i < geo.cells; i++) if (found.solution[i] !== found.second[i]) out.differ.push(i);
            return out;
        }
        out.solution = found.solution;
        out.grade = S.grade(geo, values, { maxTier: 7, unique: true, solution: found.solution });
        out.level = levelOfTier(out.grade.tier);
        return out;
    }

    S.LEVELS = LEVELS;
    S.LEVEL_ORDER = LEVEL_ORDER;
    S.SYMMETRY = SYMMETRY;
    S.levelOfTier = levelOfTier;
    S.orbits = orbits;
    S.dailySeed = dailySeed;
    S.generate = generate;
    S.analyze = analyze;
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
